import { useState } from "react";
import { createPlatformLead } from "../lib/rangegridApi";

const LANDING_SIGNALS = [
  "Owner command",
  "Member self-service",
  "Custom domains",
  "Scoped workspaces",
  "Review queues",
  "Insights"
];

const LANDING_PREVIEW_ITEMS = [
  {
    label: "Applicant ready for review",
    detail: "Screening answers, waiver, and payment stage in one place."
  },
  {
    label: "1,000-yard lane reserved",
    detail: "Booking follows hours, duration caps, and member limits."
  },
  {
    label: "Class awaiting roster signoff",
    detail: "Chapter lead submits completion for owner visibility."
  },
  {
    label: "Stripe setup pending",
    detail: "Payment readiness stays clear before public checkout."
  },
  {
    label: "Public website live",
    detail: "Custom domain routes visitors directly to the range."
  }
];

const LANDING_WORKFLOW = [
  {
    title: "Public website",
    body: "Show the range, capture interest, and route members into the right next step."
  },
  {
    title: "Member application",
    body: "Collect screening answers, waivers, and required onboarding details."
  },
  {
    title: "Owner review",
    body: "Approve, reject, or request more information from one applicant record."
  },
  {
    title: "Payment",
    body: "Collect membership or event payments and keep receipts attached."
  },
  {
    title: "Reservation",
    body: "Let eligible members book resources within owner-defined limits."
  },
  {
    title: "Member visit",
    body: "Keep attendance, requirements, and future activity visible."
  }
];

const LANDING_MODULES = [
  {
    title: "Membership and screening",
    body: "Publish applications, collect waivers, review background gates, and keep owner decisions tied to each member record.",
    tone: "primary"
  },
  {
    title: "Reservations and resources",
    body: "Set reservable lanes, bays, duration caps, booking windows, closures, and member limits from the same resource model.",
    tone: "quiet"
  },
  {
    title: "Programs and events",
    body: "Build classes, manage rosters, approve chapter requests, record attendance, and track guest leads without spreadsheet handoffs.",
    tone: "quiet"
  },
  {
    title: "Payments and public websites",
    body: "Connect Stripe, publish a public range site, collect interest, and keep receipts attached to the workflow that created them.",
    tone: "quiet"
  }
];

const LANDING_READINESS = [
  "Private member ranges",
  "Commercial ranges",
  "Training businesses",
  "Club and chapter organizations"
];

const OPERATION_TYPES = [
  { value: "", label: "Select one" },
  { value: "private_range", label: "Private range" },
  { value: "commercial_range", label: "Commercial range" },
  { value: "training_business", label: "Training business" },
  { value: "club_or_chapter", label: "Club or chapter organization" },
  { value: "other", label: "Other" }
];

const initialForm = {
  name: "",
  email: "",
  phone: "",
  operationType: "",
  businessName: "",
  message: ""
};

export function LandingPage() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  function updateForm(patch) {
    setForm((current) => ({ ...current, ...patch }));
    setSubmitted(false);
    setError("");
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await createPlatformLead({
        ...form,
        sourcePage: "rangegrid.app"
      });
      setSubmitted(true);
      setForm(initialForm);
    } catch (err) {
      setError(err.message || "RangeGrid could not send this request yet.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="landing-page">
      <nav className="landing-nav" aria-label="RangeGrid">
        <a className="landing-brand" href="/">
          <span className="landing-brand-mark">RG</span>
          <strong>RangeGrid</strong>
        </a>
        <div className="landing-nav-actions">
          <a href="#demo">Request a demo</a>
          <a href="/console?start=owner">Create workspace</a>
        </div>
      </nav>

      <section className="landing-hero">
        <div className="landing-hero-copy">
          <span className="landing-kicker">Range operations software</span>
          <h1>The operating system for modern shooting ranges.</h1>
          <p>
            Run memberships, reservations, events, payments, staff access, and member self-service from one connected platform.
          </p>
          <div className="landing-hero-actions">
            <a className="landing-primary-action" href="#demo">Request a demo</a>
            <a className="landing-secondary-action" href="/console?start=owner">Create workspace</a>
          </div>
        </div>

        <aside className="landing-ops-panel" aria-label="RangeGrid workflow preview">
          <div className="landing-ops-topline">
            <span>Owner command</span>
            <strong>Today needs a decision.</strong>
          </div>
          {LANDING_PREVIEW_ITEMS.map((item) => (
            <div className="landing-ops-list" key={item.label}>
              <strong>{item.label}</strong>
              <em>{item.detail}</em>
            </div>
          ))}
        </aside>
      </section>

      <section className="landing-strip" aria-label="RangeGrid capabilities">
        {LANDING_SIGNALS.map((signal) => (
          <span key={signal}>{signal}</span>
        ))}
      </section>

      <section className="landing-section landing-flow-section" aria-label="How RangeGrid works">
        <div className="landing-section-intro">
          <h2>One connected member journey.</h2>
          <p>
            RangeGrid keeps each step connected, from the first public inquiry to the member's next reservation.
          </p>
        </div>
        <div className="landing-flow-grid">
          {LANDING_WORKFLOW.map((step) => (
            <article className="landing-flow-step" key={step.title}>
              <strong>{step.title}</strong>
              <p>{step.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="landing-module-grid" aria-label="Core modules">
        {LANDING_MODULES.map((module) => (
          <article key={module.title} className={`landing-module-card is-${module.tone}`}>
            <strong>{module.title}</strong>
            <p>{module.body}</p>
          </article>
        ))}
      </section>

      <section className="landing-section landing-proof">
        <div>
          <h2>Built for real range operations.</h2>
          <p>
            Use the same platform for member-only facilities, training operations, clubs, chapters, and commercial ranges.
          </p>
        </div>
        <div className="landing-proof-list">
          <div className="landing-readiness-list" aria-label="Operation types">
            {LANDING_READINESS.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
          <div>
            <strong>Owner control</strong>
            <span>Configure public websites, member gates, payment flows, roles, chapters, resources, and availability.</span>
          </div>
          <div>
            <strong>Member self-service</strong>
            <span>Members can see operating status, book eligible resources, complete requirements, and track payments.</span>
          </div>
          <div>
            <strong>Operator focus</strong>
            <span>Chapter leads and staff get scoped workspaces with only the applications and actions they need.</span>
          </div>
        </div>
      </section>

      <section id="demo" className="landing-demo-section">
        <div className="landing-demo-copy">
          <h2>Tell us about your range.</h2>
          <p>
            We will follow up with a focused walkthrough around your membership model, reservation needs,
            public website, and payment workflow.
          </p>
        </div>
        <form className="landing-demo-form" onSubmit={handleSubmit}>
          <label>
            <span>Name</span>
            <input
              name="name"
              value={form.name}
              onChange={(event) => updateForm({ name: event.target.value })}
              autoComplete="name"
              required
            />
          </label>
          <label>
            <span>Email</span>
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={(event) => updateForm({ email: event.target.value })}
              autoComplete="email"
              required
            />
          </label>
          <label>
            <span>Phone</span>
            <input
              name="phone"
              type="tel"
              value={form.phone}
              onChange={(event) => updateForm({ phone: event.target.value })}
              autoComplete="tel"
            />
          </label>
          <label>
            <span>Operation type</span>
            <select
              name="operationType"
              value={form.operationType}
              onChange={(event) => updateForm({ operationType: event.target.value })}
              required
            >
              {OPERATION_TYPES.map((option) => (
                <option key={option.value || "empty"} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label className="landing-demo-wide">
            <span>Range or business name</span>
            <input
              name="businessName"
              value={form.businessName}
              onChange={(event) => updateForm({ businessName: event.target.value })}
              autoComplete="organization"
              required
            />
          </label>
          <label className="landing-demo-message">
            <span>What do you want RangeGrid to help with?</span>
            <textarea
              name="message"
              value={form.message}
              onChange={(event) => updateForm({ message: event.target.value })}
              rows={5}
            />
          </label>
          <button type="submit" disabled={submitting}>
            {submitting ? "Sending request..." : "Request a demo"}
          </button>
          {submitted && <div className="landing-form-success">Demo request received. We will follow up soon.</div>}
          {error && <div className="landing-form-error">{error}</div>}
        </form>
      </section>
    </main>
  );
}
